import { Beaker, ExternalLink, Calendar } from 'lucide-react';
import { Badge } from '../ui/badge';

export function ExperimentsSection({ experiments, loading, emptyMessage }) {
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div 
            className="retro-card p-6 h-full flex flex-col"
            data-testid="experiments-section"
        >
            {/* Section Header */}
            <div className="mb-6 flex-shrink-0">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-6 h-6 bg-primary flex items-center justify-center">
                        <Beaker className="h-3 w-3 text-primary-foreground" />
                    </div>
                    <h2 
                        className="text-xl sm:text-2xl tracking-tight uppercase"
                        style={{ fontFamily: "'Silkscreen', cursive" }}
                        data-testid="experiments-title"
                    >
                        Experiments
                    </h2>
                </div>
                <p className="text-muted-foreground text-sm">
                    Side projects and wild ideas.
                </p>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto max-h-[400px] pr-2 scrollbar-thin">
                {loading ? (
                    <div className="space-y-4">
                        {Array(2).fill(0).map((_, i) => (
                            <div key={i} className="p-4 border-2 border-dashed border-border">
                                <div className="h-5 w-3/4 bg-muted skeleton-pulse mb-2" />
                                <div className="h-4 w-full bg-muted skeleton-pulse mb-2" />
                                <div className="flex gap-2">
                                    <div className="h-4 w-12 bg-muted skeleton-pulse" />
                                    <div className="h-4 w-12 bg-muted skeleton-pulse" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : experiments.length > 0 ? (
                    <div className="space-y-4">
                        {experiments.map((experiment) => (
                            <a
                                key={experiment.id}
                                href={experiment.link || '#'}
                                target={experiment.link ? '_blank' : '_self'}
                                rel="noopener noreferrer"
                                className="block p-4 border-2 border-border hover:border-primary transition-all group"
                                data-testid={`experiment-item-${experiment.id}`}
                            >
                                <h3 
                                    className="text-base tracking-tight group-hover:text-primary transition-colors flex items-start gap-2"
                                    style={{ fontFamily: "'Silkscreen', cursive" }}
                                >
                                    {experiment.title}
                                    {experiment.link && (
                                        <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                                    )}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {experiment.description}
                                </p>
                                {experiment.tech_stack && experiment.tech_stack.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {experiment.tech_stack.slice(0, 3).map((tech, index) => (
                                            <Badge 
                                                key={index}
                                                variant="outline"
                                                className="pixel-badge rounded-none text-[9px] px-1.5 py-0"
                                            >
                                                {tech}
                                            </Badge>
                                        ))}
                                        {experiment.tech_stack.length > 3 && (
                                            <Badge 
                                                variant="outline"
                                                className="pixel-badge rounded-none text-[9px] px-1.5 py-0"
                                            >
                                                +{experiment.tech_stack.length - 3}
                                            </Badge>
                                        )}
                                    </div>
                                )}
                                {/* Published Date */}
                                <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground uppercase tracking-widest">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(experiment.created_at)}
                                </div>
                            </a>
                        ))}
                    </div>
                ) : (
                    <div className="p-8 border-2 border-dashed border-border text-center">
                        <Beaker className="h-10 w-10 text-muted-foreground/30 mx-auto mb-4" />
                        <p 
                            className="text-muted-foreground italic text-sm"
                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                            data-testid="experiments-empty-message"
                        >
                            {emptyMessage}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
